import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router';

import { LoginPage } from '@/features/auth/LoginPage';
import { SESSION_QUERY_KEY, useSession } from '@/features/auth/useSession';
import { LoadingState, refusalOf } from '@/shared/components/States';
import { useResetToasts } from '@/shared/hooks/useToast';
import type { SessionPayload } from '@/shared/types/domain';

import {
  WORKSPACE_HOME,
  forgetOtherPeople,
  identityKey,
  mayOpen,
  relationToShown,
  rememberShown,
  startAfresh,
  type IdentityParts,
  type Relation,
} from './workspaceIdentity';

const TenantMfaGate = lazy(async () => ({
  default: (await import('@/features/auth/mfa/TenantMfaGate')).TenantMfaGate,
}));

function Loading() {
  return (
    <div className="mx-auto max-w-md p-8">
      <LoadingState rows={3} />
    </div>
  );
}

/**
 * Gates the authenticated shell.
 *
 * The login screen is rendered in place rather than redirected to, so a user
 * whose session expired mid-task returns to the same URL after signing in.
 *
 * A session whose password was right but whose MFA step is pending renders the
 * MFA step instead — never the shell, so nothing privileged is rendered or
 * fetched for it. `holding` keeps that step on screen after the server already
 * counts the session as complete, for the one moment that must not be cut
 * short: the recovery codes, shown once, before they are acknowledged.
 *
 * OBS-007 / R19 — and the shell renders only for the identity its address and
 * its cache belong to (`useSafeLanding`).
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { session, isAuthenticated, isLoading, mfaPending, pendingIdentity } = useSession();
  const [holding, setHolding] = useState(false);
  // Decided only for a session the shell is about to render for: never under the MFA step.
  const landing = useSafeLanding(isAuthenticated && !holding ? session : null);

  if (isLoading && !holding) return <Loading />;

  if (mfaPending !== null || holding) {
    return (
      <Suspense fallback={<Loading />}>
        <SignInShown onShown={landing.interrupt} />
        <TenantMfaGate state={mfaPending} identity={pendingIdentity} onHoldChange={setHolding} />
      </Suspense>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <SignInShown onShown={landing.interrupt} />
        <LoginPage />
      </>
    );
  }

  if (!landing.ready) return <Loading />;

  return children;
}

/** Tells the gate that the sign-in, or the MFA step, has taken the screen. */
function SignInShown({ onShown }: { onShown: () => void }) {
  useEffect(() => {
    onShown();
  }, [onShown]);

  return null;
}

interface Arrival {
  key: string;
  parts: IdentityParts;
  path: string;
  /** Re-authorised to the route it had before: its first reads may still refuse it. */
  watch: boolean;
}

/**
 * OBS-007 / R19 — a safe landing when the identity on this tab changes (the
 * owner/CTO decision of 28 October 2026; R19 confirmed with reauthorization).
 *
 * - Another identity (another user, or the same user in another organisation):
 *   every cached read and mutation, the last person's recent searches and
 *   on-screen messages go, and the shell opens on the new identity's own home
 *   (guide progress is each identity's own, and stays). The address changes
 *   first, so nothing of the last identity — not its page, not its data —
 *   renders for even one frame.
 * - The same user signing in again after the session ended (an expiry, a
 *   sign-out, the MFA step): nothing read before is shown; the route is read
 *   again under the renewed session. The route's navigation gate is asked again
 *   with the fresh permissions, and until the route's first reads have
 *   answered, a read refused (403) or not found (404) sends the person home.
 * - Otherwise (a first sign-in on this tab, a session read again for the same
 *   person) nothing changes: the address is the one the person asked for.
 *
 * Nothing here grants, widens or decides access: the server still answers every
 * read, and the gate only chooses where the shell opens and what it forgets.
 */
function useSafeLanding(session: SessionPayload | null) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const resetToasts = useResetToasts();

  const signedIn = session !== null;
  const tenantId = session?.tenant?.id ?? '';
  const userId = session?.user.id ?? '';
  const parts = useMemo<IdentityParts | null>(
    () => (signedIn ? [tenantId, userId] : null),
    [signedIn, tenantId, userId],
  );
  const key = parts === null ? null : identityKey(parts);

  // Whose address and cache these are: the identity the shell last rendered for.
  const owner = useRef<string | null>(null);
  // The sign-in or the MFA step has taken the screen since then.
  const interrupted = useRef(false);
  const [ready, setReady] = useState<string | null>(null);
  const [arrival, setArrival] = useState<Arrival | null>(null);
  // The re-authorised route, until its first reads have answered.
  const [watching, setWatching] = useState<string | null>(null);

  const interrupt = useCallback(() => {
    interrupted.current = true;
    setReady(null);
    setArrival(null);
    setWatching(null);
    resetToasts();
  }, [resetToasts]);

  const accept = useCallback((next: Arrival) => {
    owner.current = next.key;
    interrupted.current = false;
    setArrival(null);
    setWatching(next.watch ? next.path : null);
    setReady(next.key);
    rememberShown(next.parts);
  }, []);

  // Where this identity lands — decided once, before the shell renders for it.
  useEffect(() => {
    if (session === null || parts === null || key === null) return;
    if (ready === key || arrival?.key === key) return;

    const relation: Relation =
      owner.current === null ? relationToShown(parts) : owner.current === key ? 'same' : 'other';
    let path = pathname;
    let watch = false;

    if (relation === 'other') {
      startAfresh(queryClient);
      forgetOtherPeople();
      resetToasts();
      path = WORKSPACE_HOME;
    } else if (interrupted.current) {
      // A sign-in starts from nothing read before it.
      startAfresh(queryClient);
      if (relation === 'same') {
        if (!mayOpen(pathname, session)) path = WORKSPACE_HOME;
        else watch = pathname !== WORKSPACE_HOME;
      }
    }

    const next: Arrival = { key, parts, path, watch };

    if (path === pathname) {
      accept(next);
    } else {
      setArrival(next);
      void navigate(path, { replace: true });
    }
  }, [session, parts, key, ready, arrival, pathname, queryClient, navigate, resetToasts, accept]);

  // The address is now where the identity lands: the shell may render.
  useEffect(() => {
    if (arrival !== null && pathname === arrival.path) accept(arrival);
  }, [arrival, pathname, accept]);

  // Until the re-authorised route's first reads have answered, a refusal sends the person home.
  useEffect(() => {
    if (watching === null) return;
    if (pathname !== watching) {
      setWatching(null);

      return;
    }

    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated') return;
      // The cache types its queries loosely (`Query<any, …>`); a key is a key.
      const [head] = event.query.queryKey as QueryKey;
      if (head === SESSION_QUERY_KEY[0]) return;

      if (event.action.type === 'error') {
        const refusal = refusalOf(event.action.error);

        if (refusal === 'no-access' || refusal === 'not-found') {
          setWatching(null);
          void navigate(WORKSPACE_HOME, { replace: true });

          return;
        }
      }
      if (
        (event.action.type === 'success' || event.action.type === 'error') &&
        queryClient.isFetching() === 0
      ) {
        setWatching(null);
      }
    });
  }, [watching, pathname, queryClient, navigate]);

  return { ready: key !== null && ready === key, interrupt };
}
